/**
 * Agent UI Bridge - Event Bus for Chat ↔ Shell Communication
 *
 * Enables interaction between AgentChatConsole (Layer 1) and AgentShell/custom dashboards (Layer 2)
 * following the official two-layer pattern.
 *
 * INTEGRATION POINTS:
 * - AgentShell component: agents_ui/app/src/components/agent/AgentShell.tsx (listens to shell.* events)
 * - AgentChatConsole component: agents_ui/app/src/components/agents/AgentChatConsole.tsx (listens to chat.* events)
 * - Agent task panels: emit shell.focusEntity when user clicks a row/card
 * - Quick prompts: emit shell.openPanel to jump to specific tabs
 *
 * USAGE:
 * ```tsx
 * // Open a panel
 * agentUiBridge.emit({ type: 'shell.openPanel', agent: 'booking', panelId: 'leads' })
 *
 * // Focus an entity
 * agentUiBridge.emit({ type: 'shell.focusEntity', agent: 'booking', entityType: 'lead', id: '123' })
 *
 * // Prefill chat
 * agentUiBridge.emit({ type: 'chat.prefill', agent: 'booking', text: 'Summarize this lead...', focusChat: true })
 *
 * // System message (task lifecycle)
 * agentUiBridge.emit({ type: 'chat.appendSystemMessage', agent: 'booking', text: 'Task approved: Send email to venue' })
 * ```
 */

/**
 * Entity types that can be focused in Shell
 */
export type EntityType = 'lead' | 'task' | 'venue' | 'contact' | 'event' | 'campaign' | 'release' | 'goal'

/**
 * Union type of all agent UI events
 */
export type AgentUiEvent =
  | {
      type: 'shell.openPanel'
      agent: string
      panelId: string
    }
  | {
      type: 'shell.focusEntity'
      agent: string
      entityType: EntityType
      id: string
      /** Optional panel to open (if known) */
      panelId?: string
    }
  | {
      type: 'shell.setFilters'
      agent: string
      filters: Record<string, any>
    }
  | {
      type: 'chat.prefill'
      agent: string
      text: string
      /** Whether to focus the chat input after prefill */
      focusChat?: boolean
    }
  | {
      type: 'chat.appendSystemMessage'
      agent: string
      text: string
      /** Optional metadata for the system message */
      metadata?: Record<string, any>
    }

/**
 * Event handler function type
 */
export type AgentUiEventHandler = (event: AgentUiEvent) => void

/**
 * Agent UI Bridge - Simple pub/sub event bus
 */
class AgentUiBridge {
  private handlers: Set<AgentUiEventHandler> = new Set()

  /**
   * Emit an event to all subscribers
   */
  emit(event: AgentUiEvent): void {
    // Use setTimeout to ensure event is emitted asynchronously
    // This prevents issues with React's batching and strict mode
    setTimeout(() => {
      this.handlers.forEach((handler) => {
        try {
          handler(event)
        } catch (error) {
          console.error('[AgentUiBridge] Handler error:', error)
        }
      })
    }, 0)
  }

  /**
   * Subscribe to all events
   * Returns unsubscribe function
   */
  on(handler: AgentUiEventHandler): () => void {
    this.handlers.add(handler)

    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler)
    }
  }

  /**
   * Subscribe to events for a specific agent
   * Returns unsubscribe function
   */
  onAgent(agent: string, handler: AgentUiEventHandler): () => void {
    const filteredHandler: AgentUiEventHandler = (event) => {
      if (event.agent === agent) {
        handler(event)
      }
    }

    this.handlers.add(filteredHandler)

    return () => {
      this.handlers.delete(filteredHandler)
    }
  }

  /**
   * Subscribe to specific event types
   * Returns unsubscribe function
   */
  onType<T extends AgentUiEvent['type']>(
    type: T,
    handler: (event: Extract<AgentUiEvent, { type: T }>) => void,
  ): () => void {
    const filteredHandler: AgentUiEventHandler = (event) => {
      if (event.type === type) {
        handler(event as Extract<AgentUiEvent, { type: T }>)
      }
    }

    this.handlers.add(filteredHandler)

    return () => {
      this.handlers.delete(filteredHandler)
    }
  }

  /**
   * Clear all handlers (for testing)
   */
  clear(): void {
    this.handlers.clear()
  }
}

/**
 * Singleton instance
 */
export const agentUiBridge = new AgentUiBridge()
